import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Linkedin, Instagram, MessageCircle } from 'lucide-react';
import api from './services/cloudflare-api';

export default function Footer() {
  const [contactData, setContactData] = useState(null);

  // Load contact data from D1 API
  useEffect(() => {
    const loadContactData = async () => {
      try {
        console.log('📡 Footer: Loading contact data from D1 API...');

        // Load site config for Footer
        const siteConfig = await api.getSiteConfig();

        // Transform to Footer format
        const formattedData = {
          phones: (siteConfig.phones || []).map(phone => ({
            number: phone,
            link: `tel:${phone}`
          })),
          emails: (siteConfig.emails || []).map(email => ({
            address: email,
            link: `mailto:${email}`
          })),
          social: siteConfig.social || {}
        };

        setContactData(formattedData);
        console.log('✅ Footer: Loaded contact data from D1 API');
      } catch (error) {
        console.error('❌ Footer: Failed to load contact data from D1 API:', error);
        // Fallback to default empty structure
        setContactData({
          phones: [],
          emails: [],
          social: {}
        });
      }
    };

    loadContactData();

    // Listen for contact updates from Dashboard
    const handleContactUpdate = () => {
      console.log('📢 Footer: Received contactUpdated event, reloading data...');
      loadContactData();
    };

    window.addEventListener('contactUpdated', handleContactUpdate);

    return () => {
      window.removeEventListener('contactUpdated', handleContactUpdate);
    };
  }, []);

  // If data not loaded yet, show loading or default
  if (!contactData) {
    return null;
  }

  return (
    <footer id="Footer" className="bg-white border-t border-gray-200 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-8">

          {/* Contact Section */}
          <div>
            <h3 className="text-gray-900 text-base font-semibold mb-6 tracking-wide">CONTACT</h3>
            <div className="space-y-4">
              {/* Phone Numbers */}
              {contactData.phones && contactData.phones.length > 0 ? (
                <div className="flex items-start gap-2">
                  <img
                    src="/social_icons/phone.png"
                    alt="Phone"
                    className="w-10 h-10 object-contain flex-shrink-0"
                  />
                  <div className="flex flex-col gap-1">
                    {contactData.phones.map((phone, index) => (
                      <a key={index} href={phone.link} className="text-primary hover:text-primary-dark transition-colors text-sm">
                        {phone.number}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <img
                    src="/social_icons/phone.png"
                    alt="Phone"
                    className="w-10 h-10 object-contain"
                  />
                  <a href="tel:+1-917-720-5208" className="text-primary hover:text-primary-dark transition-colors text-sm">
                    +1-917-720-5208
                  </a>
                </div>
              )}

              {/* Email Addresses */}
              {contactData.emails && contactData.emails.length > 0 ? (
                <div className="flex items-start gap-2">
                  <img
                    src="/social_icons/mail.png"
                    alt="Email"
                    className="w-10 h-10 object-contain flex-shrink-0"
                  />
                  <div className="flex flex-col gap-1">
                    {contactData.emails.map((email, index) => (
                      <a key={index} href={email.link} className="text-primary hover:text-primary-dark transition-colors text-sm">
                        {email.address}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <img
                    src="/social_icons/mail.png"
                    alt="Email"
                    className="w-10 h-10 object-contain"
                  />
                  <a href="mailto:info@mediossolutions.com" className="text-primary hover:text-primary-dark transition-colors text-sm">
                    info@mediossolutions.com
                  </a>
                </div>
              )}
              <div>
                <div className="flex flex-wrap gap-4">
                  {contactData.social?.facebook && (
                    <a href={contactData.social.facebook} target="_blank" rel="noopener noreferrer" className="group" title="Facebook">
                      <img
                        src="/social_icons/facebook.png"
                        alt="Facebook"
                        className="w-10 h-10 object-contain transform group-hover:scale-110 transition-transform"
                      />
                    </a>
                  )}
                  {contactData.social?.linkedin && (
                    <a href={contactData.social.linkedin} target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-gray-200 p-2 rounded-lg transition-all hover:scale-110 text-gray-900 group" title="LinkedIn">
                      <Linkedin className="w-6 h-6" />
                    </a>
                  )}
                  {contactData.social?.instagram && (
                    <a href={contactData.social.instagram} target="_blank" rel="noopener noreferrer" className="group" title="Instagram">
                      <img
                        src="/social_icons/insta.png"
                        alt="Instagram"
                        className="w-10 h-10 object-contain transform group-hover:scale-110 transition-transform"
                      />
                    </a>
                  )}
                  {contactData.social?.twitter && (
                    <a href={contactData.social.twitter} target="_blank" rel="noopener noreferrer" className="group" title="X (Twitter)">
                      <img
                        src="/social_icons/x.png"
                        alt="X (Twitter)"
                        className="w-10 h-10 object-contain transform group-hover:scale-110 transition-transform"
                      />
                    </a>
                  )}
                  {contactData.social?.reddit && (
                    <a href={contactData.social.reddit} target="_blank" rel="noopener noreferrer" className="bg-gray-100 hover:bg-gray-200 p-2 rounded-lg transition-all hover:scale-110 text-gray-900" title="Reddit">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Useful Links Section */}
          <div>
            <h3 className="text-gray-900 text-base font-semibold mb-6 tracking-wide">USEFUL LINKS</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/home" className="text-sm text-gray-600 hover:text-primary transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-sm text-gray-600 hover:text-primary transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/refund-policy" className="text-sm text-gray-600 hover:text-primary transition-colors">Refund & Return Policy</Link>
              </li>
              <li>
                <Link to="/credit-card-security" className="text-sm text-gray-600 hover:text-primary transition-colors">Credit Card Security</Link>
              </li>
              <li>
                <Link to="/terms-conditions" className="text-sm text-gray-600 hover:text-primary transition-colors">Terms & Conditions</Link>
              </li>
            </ul>
          </div>

          {/* Services Section */}
          <div>
            <h3 className="text-gray-900 text-base font-semibold mb-6 tracking-wide">SERVICES</h3>
            <ul className="space-y-3">
              <li>
                <a href="#" className="text-sm text-gray-600 hover:text-primary transition-colors">Real Estate & Architectural Retouch</a>
              </li>
              <li>
                <a href="#" className="text-sm text-gray-600 hover:text-primary transition-colors">Drafting & 3D Rendering</a>
              </li>
              <li>
                <a href="#" className="text-sm text-gray-600 hover:text-primary transition-colors">People And Product Retouching</a>
              </li>
              <li>
                <a href="#" className="text-sm text-gray-600 hover:text-primary transition-colors">Extraction & Banner/Book Design</a>
              </li>
              <li>
                <a href="#" className="text-sm text-gray-600 hover:text-primary transition-colors">Realtor And Property Website Development And Marketing</a>
              </li>
            </ul>
          </div>

          {/* Location & Blogs Section */}
          <div>
            <div className="mb-8">
              <h3 className="text-gray-900 text-base font-semibold mb-6 tracking-wide">LOCATION</h3>
              <select className="w-full bg-gray-50 text-gray-700 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors">
                <option>Select Location</option>
                <option>New York, USA</option>
                <option>London, UK</option>
                <option>Mumbai, India</option>
              </select>
            </div>
            <div>
              <h3 className="text-gray-900 text-base font-semibold mb-4 flex items-center gap-2 tracking-wide">
                <MessageCircle className="w-5 h-5" />
                BLOGS
              </h3>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-4 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">© 2025, Medios Solutions. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
